"use client";

import type { Metadata } from "next";
import { useState, useEffect } from "react";
import Link from "next/link";
import { ChevronRight } from "lucide-react";
import { PageShell } from "@/components/layout/PageShell";
import { CtaBlock } from "@/components/CtaBlock";
import { PageHero } from "@/components/PageHero";
import { categories } from "@/lib/categories";
import { buildMetadata } from "@/lib/seo";

// export const metadata: Metadata = buildMetadata({
//   title: "Industries Served | Mining, Construction & Infrastructure Equipment",
//   description:
//     "Kasai Equipment supports construction, mining, road infrastructure, agriculture, manufacturing, and material handling operations with dependable heavy machinery.",
//   path: "/industry",
// });

const industries = [
  {
    id: 1,
    slug: "construction-projects",
    name: "Construction Projects",
    verb: "Build Stronger",
    tagColor: "bg-primary text-white",
    image: "/assets/Construction.webp",
    description:
      "Reliable equipment support for commercial buildings, roads, housing, and urban development projects.",
    benefits: [
      "Site-ready machinery",
      "Faster earthmoving support ",
      "Strong performance on rough terrain",
      "Reliable project execution",
    ],
    machines: [
      {
        productId: "excavators",
        model: "Excavators",
        spec: "Powerful Digging for Every Job Site.",
      },
      {
        productId: "loaders",
        model: "Loaders",
        spec: "One Machine. Multiple Applications.",
      },
      {
        productId: "road-machinery",
        model: "Road Machinery",
        spec: "Precision Grading Made Easy",
      },
    ],
  },
  {
    id: 2,
    slug: "mining-operations",
    name: "Mining Operations",
    verb: "Mine Deeper",
    tagColor: "bg-primary text-white",
    image: "/assets/mining_1.webp",
    description:
      "Heavy equipment solutions built for demanding extraction and material handling environments.",
    benefits: [
      "High-performance machines",
      "Support for continuous operations",
      "Equipment for remote locations",
      "Durable mining performance",
    ],
    machines: [
      {
        productId: "excavators",
        model: "Heavy Excavators",
        spec: "Maximum Digging Power for Large-Scale Operation",
      },
      {
        productId: "loaders",
        model: "Wheel Loaders",
        spec: "Heavy-Duty Loading for Tough Mining Sites",
      },
      {
        productId: "road-machinery",
        model: "Road Machinery",
        spec: "Precision Road Maintenance for Mining Sites",
      },
    ],
  },
  {
    id: 3,
    slug: "road-infrastructure-development",
    name: "Road & Infrastructure",
    verb: "Move Infrastructure Forward",
    tagColor: "bg-primary text-white",
    image: "/assets/infra.webp",
    description:
      "Equipment designed to support highways, bridges, drainage systems, and public works projects.",
    benefits: [
      "Efficient roadwork support",
      "Heavy-duty site equipment",
      "Reliable grading performance",
      "Built for large-scale development",
    ],
    machines: [
      {
        productId: "excavators",
        model: "Excavators",
        spec: "Compact Performance for Urban Construction",
      },
      {
        productId: "forklifts",
        model: "Forklift",
        spec: "Road construction and surface preparation",
      },
      {
        productId: "loaders",
        model: "Loaders",
        spec: "Fast Loading for Maximum Site Productivity",
      },
    ],
  },
  {
    id: 4,
    slug: "agricultural-development",
    name: "Agricultural Development",
    verb: "Grow Better",
    tagColor: "bg-primary text-white",
    image: "/assets/Agri.webp",
    description:
      "Machinery solutions that help improve farming productivity and land preparation work.",
    benefits: [
      "Land clearing and preparation support",
      "Equipment for farming and rural operations",
      "Better field productivity and workflow",
      "Reliable support for agricultural development",
    ],
    machines: [
      {
        productId: "excavators",
        model: "Excavator",
        spec: "Compact Digging for Farm Projects",
      },
      {
        productId: "loaders",
        model: "Loaders",
        spec: "Versatile Power for Agriculture and Construction",
      },
      {
        productId: "forklifts",
        model: "Forklifts",
        spec: "Smart Material Handling for Agriculture",
      },
    ],
  },
  {
    id: 5,
    slug: "manufacturing-industry",
    name: "Manufacturing Industry",
    verb: "Power Production",
    tagColor: "bg-primary text-white",
    image: "/assets/ware.webp",
    description:
      "Reliable machinery support for industrial plants, factories, and production facilities.",
    benefits: [
      "Support for factory operations",
      "Efficient workflow handling",
      "Reduced machine downtime",
      "Dependable industrial equipment",
    ],
    machines: [
      {
        productId: "mewps",
        model: "MEWPS",
        spec: "Flexible Reach for Industrial Facilities",
      },
      {
        productId: "loaders",
        model: "Loader",
        spec: "Compact Power for Industrial Tasks",
      },
      {
        productId: "forklifts",
        model: "Forklift",
        spec: "Move Materials with Speed and Precision",
      },
    ],
  },
];

export default function IndustryPage() {
  const [activeSection, setActiveSection] = useState<string>("construction-projects");

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "-50% 0px -50% 0px",
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    }, observerOptions);

    // Observe all industry sections
    industries.forEach((ind) => {
      const element = document.getElementById(ind.slug);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <PageShell>
      <PageHero
        eyebrow="INDUSTRIES"
        title="The Power Behind Congo's Progress"
        highlightWord="Progress"
        description="Kasai supports growing industries with dependable equipment, technical support, and field-ready solutions designed for real project conditions. From remote mining zones to expanding city infrastructure, we help businesses work with confidence and efficiency."
      />

      {/* Jump Nav */}
      <div className="sticky top-[88px] sm:top-[92px] lg:top-[108px] z-30 bg-background/95 backdrop-blur border-b border-border">
        <div className="mx-auto max-w-[1440px] px-4 py-2.5 sm:py-3 flex gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {industries.map((ind) => (
            <a
              key={ind.id}
              href={`#${ind.slug}`}
              className={`flex-shrink-0 px-3.5 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-full border transition-colors whitespace-nowrap ${
                activeSection === ind.slug
                  ? "bg-primary text-white border-primary"
                  : "border-border hover:border-primary hover:text-primary"
              }`}
            >
              {ind.name}
            </a>
          ))}
        </div>
      </div>

      {industries.map((ind) => (
        <section key={ind.id} id={ind.slug} className="scroll-mt-40">
          <div className="relative h-64 md:h-80">
            <img
              src={ind.image}
              alt={`${ind.name} heavy equipment solutions by Kasai Equipment`}
              className={`w-full h-full object-cover ${ind.slug === "mining-operations" ? "object-center" : "object-top"
                }`}
              loading="lazy"
            />

            <div className="absolute inset-0 bg-foreground/50" />

            <div className="absolute inset-0 mx-auto max-w-[1440px] px-4  flex items-end pb-8">
              <div>
                <span
                  className={`inline-block text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-2 ${ind.tagColor}`}
                >
                  {ind.name}
                </span>

                <h2 className="text-base font-bold text-white">
                  {ind.verb} with Kasai
                </h2>
              </div>
            </div>
          </div>

          <div className="mx-auto max-w-[1440px] px-4  py-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <p className="text-muted-foreground mb-6">
                  {ind.description}
                </p>

                <ul className="space-y-2">
                  {ind.benefits.map((benefit) => (
                    <li
                      key={benefit}
                      className="flex items-start gap-2 text-sm"
                    >
                      <ChevronRight className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-3">
                <h3 className="font-semibold mb-3">Recommended Machines</h3>

                {ind.machines.map((machine) => {
                  const category = categories.find((cat) => cat.slug === machine.productId);

                  return (
                  <Link
                    key={machine.productId}
                    href={`/products/${machine.productId}`}
                    className="block bg-card border border-border rounded-md p-4 hover:border-primary transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <img
                        src={category?.image || ind.image}
                        alt={category ? `${category.name} machinery` : `${machine.model} machinery`}
                        className="w-[40px] h-[30px] shrink-0"
                      />

                      <div className="flex items-center justify-between w-full min-w-0">
                        <div className="min-w-0">
                          <p className="font-semibold truncate">{machine.model}</p>
                          <p className="text-sm text-muted-foreground truncate">
                            {machine.spec}
                          </p>
                        </div>

                        <span className="text-primary text-sm font-medium flex items-center gap-1 whitespace-nowrap shrink-0">
                          View
                          <ChevronRight className="w-3.5 h-3.5" />
                        </span>
                      </div>
                    </div>
                  </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      ))}

      <CtaBlock
        title="Looking for Industry-Specific Solutions?"
        text="Our experts help you choose the right industrial solutions and equipment for your business."
        primary={{ to: "/products", label: "Explore Products" }}
        secondary={{ to: "/contact", label: "Contact Us" }}
      />
    </PageShell>
  );
}
